import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  #classObserver;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);

    // Support fallback events from cached templates/grids
    document.addEventListener('cart:updated', this.#handleForceOpen);
    document.addEventListener('cart:add', this.#handleForceOpen);

    // Observe class changes to trigger open when class 'open', 'is-open', or 'active' is added
    this.#classObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName === 'class') {
          const hasOpenClass = ['open', 'is-open', 'active'].some(cls => this.classList.contains(cls));
          if (hasOpenClass && this.refs?.dialog && !this.refs.dialog.open) {
            this.open();
            break;
          }
        }
      }
    });
    this.#classObserver.observe(this, { attributes: true, attributeFilter: ['class'] });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('cart:updated', this.#handleForceOpen);
    document.removeEventListener('cart:add', this.#handleForceOpen);
    if (this.#classObserver) {
      this.#classObserver.disconnect();
    }
  }

  #handleCartAdd = (event) => {
    const isQuickAdd = event.target?.closest?.('quick-add-component, .quick-add-modal');
    if (this.hasAttribute('auto-open') || isQuickAdd) {
      this.showDialog();
    }
  };

  #handleForceOpen = () => {
    this.open();
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
